<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useCartFunctions } from '@/helpers/cart-actions';
import GiftCardEditorModal from './GiftCardEditor/EditorModal.vue';
import CartItem from './CartItem.vue';
import useContext from '@/composables/useContext';
import CartGiftCardPriceItem from './CartGiftCardPriceItem.vue';
import ConfiguratorChevron from './ProductConfigurator/ConfiguratorChevron.vue';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
});

const availableChannels = inject('$availableChannelsReactive');
const { websiteText } = useWebsiteTexts();
const { formatPrice, isAuthorized, navigateToLoginPage } = useContext();
const { removeGroupFromCart } = useCartFunctions();
const isPriceExpanded = ref(false);
const giftCardModal = ref(null);
const giftCardName = computed(() => {
  const giftcard = websiteText('giftcard__giftcard').value || 'giftcard__giftcard';
  return `${giftcard} ${props.row.variantName}` || '';
});

const mappedProduct = computed(() => {
  return {
    variantName: props.row.variantName,
    giftCardName: giftCardLocaleName.value,
    id: props.row.id,
    quantity: props.row.quantity,
    currentPrice: currentPrice.value,
    originalPrice: originalPrice.value,
    articleNumber: props.row.articleNumber,
    giftCardFees: props.row.giftCardFees,
    totalPrice: props.row.totalPrice,
  };
});

const subtotalPrice = computed(() => {
  const fees = props.row.giftCardFees.reduce((acc, curr) => {
    return acc + curr.totalRow;
  }, 0);

  return props.row.totalPrice + fees;
});

const currentPrice = computed(() => {
  return props.row.unitPrice ?? props.row.unitListPrice;
});

const originalPrice = computed(() => {
  return props.row.originalPrice >= currentPrice.value ? props.row.originalPrice : currentPrice.value;
});

const campaignPrice = computed(() => {
  const current = currentPrice.value;
  const original = originalPrice.value;

  if (current < original) {
    return formatPrice(Math.abs(((original - current) / original) * -100), undefined, '', '', 'decimal');
  }

  return 0;
});

const giftCardLocaleName = computed(() => {
  if (!props.row.giftCardInput?.giftCardCurrencyId) return '';
  const giftCardLocale = availableChannels.value?.data
    ?.find(c => c.currencySymbol === props.row.giftCardInput.giftCardCurrencyId) || {};

  if (!giftCardLocale?.name) return undefined;

  return `- ${giftCardLocale.name}`;
});

async function removeProduct() {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  const { _, error } = await removeGroupFromCart(props.row.rowGroupId);
}

function editGiftCard() {
  const input = {
    ...props.row.giftCardInput,
    orderRowSystemId: props.row.id,
  };
  giftCardModal.value?.editGiftCard(input);
}

</script>

<template>
  <div class="cart-gift-card-item">
    <div class="cart-gift-card-item__toolbar">
      <div>
        <div class="cart-gift-card-item__title">
          {{ giftCardName }}
        </div>
      </div>
      <button
        class="cart-gift-card-item__edit gift-card-cart-edit secondary-button"
        @click="editGiftCard()"
      >
        {{ websiteText('global__edit').value }}
      </button>
    </div>
    <div class="cart-gift-card-item__rows">
      <CartItem
        :campaign-percentage="campaignPrice"
        :row="props.row"
      />
    </div>
    <div class="cart-gift-card-item__remove-group">
      <button
        class="cart-gift-card-item__remove-group-button"
        @click="removeProduct()"
      >
        {{ websiteText('global__remove').value }}
        <img src="../static/icons/trash_icon.svg" alt="trash icon">
      </button>
    </div>
    <div class="cart-gift-card-item__pricing">
      <div
        class="cart-gift-card-item__pricing-summary"
        @click="isPriceExpanded = !isPriceExpanded"
      >
        <div class="cart-gift-card-item__subtotal">
          <div>
            {{ websiteText('global__subtotal').value }}
          </div>
          <div class="cart-gift-card-item__subtotal-price">
            <div>{{ formatPrice(subtotalPrice) }}</div>
            <ConfiguratorChevron
              class="cart-gift-card-item__subtotal-chevron"
              :open="isPriceExpanded"
            ></ConfiguratorChevron>
          </div>
        </div>
      </div>
      <div v-if="isPriceExpanded" class="cart-gift-card-item__pricing-content">
        <div class="cart-gift-card-item__price-items">
          <CartGiftCardPriceItem
            :item="mappedProduct"
          />
        </div>
      </div>
    </div>
    <GiftCardEditorModal ref="giftCardModal" />
  </div>
</template>

<style>
.cart-gift-card-item {
  padding: 10px 12px 6.5px;
  box-shadow: 4px 5px 20px rgba(0, 81, 121, 0.07);
  border-radius: 3px;
  background-color: #fff;
  margin-bottom: 18px;
  color: var(--color-text-darker);
}

button.cart-gift-card-item__edit {
  cursor: pointer;
  padding: 11.5px 20px;
  height: auto;
}

.cart-gift-card-item__toolbar {
  display: flex;
  justify-content: space-between;
}

.cart-gift-card-item__title {
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
}

.cart-gift-card-item__decorated {
  font-size: 12px;
  line-height: 16px;
  font-style: italic;
  margin-top: 3px;
}

.cart-gift-card-item__rows {
  margin: 10px 0 8px;
  display: grid;
  gap: 10px;
}

.cart-gift-card-item__remove-group {
  margin-bottom: 10px;
}

button.cart-gift-card-item__remove-group-button {
  margin-left: auto;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 17px;
  color: var(--color-neutrals-80);
  font-weight: 700;
  padding: 0;
}

.cart-gift-card-item__pricing {
  border-top: 1px solid var(--color-neutrals-20);
  margin: 0 -12px;
  padding: 0 12px;
}

.cart-gift-card-item__pricing-summary {
  cursor: pointer;
}

.cart-gift-card-item__subtotal {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 6.5px;
}

.cart-gift-card-item__subtotal-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-gift-card-item__subtotal-chevron {
  margin-left: 9px;
}

.cart-gift-card-item__price-items {
  display: grid;
  gap: 8px;
  margin-bottom: 10px;
  padding-right: 16px;
}

.cart-gift-card-item__price-items-title {
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-gift-card-item__pricing-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 26px;
  color: #1a1a1a;
}

.cart-gift-card-item__pricing-content {
  border-top: 1px solid var(--color-neutrals-20);
  margin: 6.5px -12px 0;
  padding: 9.5px 12px 8px;
}
</style>
